import { Accordion, AccordionDetails, AccordionSummary, Alert, Badge, Checkbox, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { useFormik } from 'formik';
import request from '../../../utils/request';
import * as Yup from 'yup';
import { FC, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import SnackbarAlert from '../../common/SnackbarAlert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import { useDispatch, useSelector } from 'react-redux';
import { selectEndorsmentFormData, updateEndorsementFormData } from 'src/features/rfqDataSlice';

type UploadSumInsuredGroupWithActionProp = {
    closeWindow: any;
    groupId: any;
    sectionId: any;
    rfqId: any;
    productId: any;
    policyId: any;
    isGmc: boolean;
    headerList?: any;
};

const UploadSumInsuredGroupWithAction: FC<UploadSumInsuredGroupWithActionProp> = ({
    closeWindow,
    groupId,
    sectionId,
    rfqId,
    productId,
    policyId,
    isGmc = false,
    headerList = []
}) => {
    const fileRef = useRef(null);
    const [loader, setLoader] = useState(false);
    const [saveExcel, setSaveExcel] = useState(false);
    const [validateExcel, setValidateExcel] = useState(true);
    const [validatedResponse, setValidatedResponse] = useState(null)
    const [showSnackBarAlert, setShowSnackBarAlert] = useState({
        status: false,
        type: 'success',
        message: null
    });
    const [errorObjects, setErrorObjects] = useState([]);
    const [errorType, setErrorType] = useState([]);
    const dispatch = useDispatch();
    const endorsementFormData = useSelector(selectEndorsmentFormData);
    const handleSnackbarClose = () => {
        setShowSnackBarAlert({ status: false, type: null, message: null });
    };

    const groupSchema = Yup.object().shape({
        action: Yup.string().required('Action is required'),
        attachment: Yup.mixed()
            .required('File is required')
            .test("is-file-of-correct-type", "File is not of supported type", () => {
                let valid = true;
                const files = fileRef?.current?.files;
                if (files) {
                    const fileArr = Array.from(files);
                    fileArr.forEach((file: any) => {
                        const type = file.type.split("/")[1];
                        const validTypes = ["xlsx", "csv", "vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
                        if (!validTypes.includes(type)) {
                            valid = false;
                        }
                    });
                }
                return valid;
            }),
        deleteGroupingHeader: Yup.array().when('action', {
            is: 'Delete',
            then: (schema) =>
                schema.min(1, 'Select at least one grouping Header'),
        }),
    });

    const formik = useFormik({
        initialValues: {
            rfqid: rfqId,
            groupId: groupId ? groupId : null,
            sectionId: sectionId,
            attachment: '',
            action: '',  // NEW FIELD,
            deleteGroupingHeader: [],
        },
        validationSchema: groupSchema,
        onSubmit: async (values) => {
            if (!values.attachment) return;

            setLoader(true); setErrorObjects([]); setErrorType([]);
            const formData = new FormData();
            formData.append('rfqid', values.rfqid);
            if (values.groupId) {
                formData.append('groupId', values.groupId);
            }
            formData.append('sectionId', values.sectionId);
            formData.append('file', values.attachment);
            formData.append('productId', productId);
            formData.append('action', values.action); // ✅ include action
            formData.append('policyId', policyId);
            formData.append('deleteGroupingHeaders', JSON.stringify(values.deleteGroupingHeader));


            request({
                url: 'endorsement/upload-excel',
                method: 'POST',
                data: formData
            })
                .then((response) => {
                    // --- SAME RESPONSE HANDLING AS BEFORE ---
                    if (response['status']) {
                        setShowSnackBarAlert({ status: true, type: 'success', message: response['message'] });
                        setLoader(false);
                        setSaveExcel(true);
                        setValidateExcel(false);
                        setValidatedResponse({
                            action: values.action,
                            excelPath: response['filePath'],
                            summary: response['data'],
                        })
                    } else {

                        if (response['message'] === 'Mandatory Validation failed') {
                            setShowSnackBarAlert({
                                status: true,
                                type: 'error',
                                message: 'Validation failed'
                            });
                            setLoader(false);
                            setErrorObjects(response['data']);
                        }
                        else if (response['message'] === 'Type Validation failed') {
                            setShowSnackBarAlert({
                                status: true,
                                type: 'error',
                                message: 'Validation failed'
                            });
                            setLoader(false);
                            setErrorType(response['data']);
                            if (response['data']?.missingValues) {
                                setErrorObjects(response['data']);
                            }

                        } else {
                            setShowSnackBarAlert({ status: true, type: 'error', message: response['message'] });
                            setLoader(false);
                            setValidatedResponse({
                                action: '',
                                excelPath: '',
                                summary: response['data'],
                            })
                        }

                    }
                })
                .catch(() => setLoader(false));
        }
    });

    const saveUploadedFile = () => {
        if (!validatedResponse) return;

        const action = validatedResponse.action;

        const sectionData =
            endorsementFormData.sumInsuredSectionData[sectionId] || { groups: {} };

        const groupData = sectionData.groups[groupId] || {
            sumInsuredEntry: [],
        };


        const updatedGroup = {
            id: groupId,
            ...groupData,
            ...(action === "Add" && {
                excelPathForInsert: validatedResponse.excelPath,
                excelInsertSummary: validatedResponse.summary,
            }),
            ...(action === "Delete" && {
                excelPathForDelete: validatedResponse.excelPath,
                excelDeleteSummary: validatedResponse.summary,
                deleteGroupingHeaders: formik.values?.deleteGroupingHeader || []
            }),
        };

        const updatedSection = {
            ...sectionData,
            groups: {
                ...sectionData.groups,
                [groupId]: updatedGroup,
            },
        };

        const updatedFormData = {
            sumInsuredSectionData: {
                ...endorsementFormData.sumInsuredSectionData,
                [sectionId]: updatedSection,
            },
        };

        // ✅ dispatch with the new object
        dispatch(updateEndorsementFormData(updatedFormData));

        const ActionMsg =
            action === "Add"
                ? "Data added successfully"
                : action === "Delete"
                    ? "Data removed successfully"
                    : "";

        setShowSnackBarAlert({
            status: true,
            type: "success",
            message: ActionMsg,
        });

        setTimeout(() => {
            console.log("updatedFormData", updatedFormData); // ✅ always the new state
            closeWindow();
        }, 2000);
    };

    const deleteGroupingOptions = headerList?.filter((item) =>
        ["employeeNo", "tpaId"].includes(
            item?.sumInsuredHeader?.attribute
        )
    )
        ?.map((item) => ({
            label: item.title,
            value: item.sumInsuredHeader.attribute,
        })) || [];

    const showDeleteOption = headerList?.some(
        (item) =>
            item?.sumInsuredHeader?.attribute == "employeeNo" ||
            item?.sumInsuredHeader?.attribute == "tpaId"
    );





    return (
        <Dialog open={true} onClose={(event, reason) => { if (reason !== 'backdropClick') { closeWindow(); } }}
            disableEscapeKeyDown fullWidth maxWidth={'sm'}>
            <SnackbarAlert showSnackBarAlert={showSnackBarAlert} handleSnackbarClose={handleSnackbarClose} />
            <DialogTitle>Bulk create and delete with Excel</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent style={{ paddingTop: 0, minHeight: 150 }}>
                    <Grid container spacing={2} mt={1}>




                        {/* Action Dropdown */}
                        <Grid item xs={12}>
                            <FormControl size="small" fullWidth error={formik.touched.action && Boolean(formik.errors.action)}>
                                <InputLabel id="action-label">Action Type  <span style={{ color: 'red' }}>*</span> </InputLabel>
                                <Select
                                    labelId="action-label"
                                    label="Action Type"
                                    value={formik.values.action}
                                    onChange={(e) => formik.setFieldValue("action", e.target.value)}
                                >
                                    <MenuItem value="Add">Create</MenuItem>
                                    {showDeleteOption && (
                                        <MenuItem value="Delete">Delete</MenuItem>
                                    )}
                                    {/* {isGmc && <MenuItem value="Delete">Delete</MenuItem>} */}

                                </Select>
                                <FormHelperText>{formik.touched.action && formik.errors.action}</FormHelperText>
                            </FormControl>
                        </Grid>

                          {
                            formik.values.action === "Delete" && <Grid item xs={12} style={{ marginTop: '5px' }} >

                                <Alert severity="warning">
                                    Cancellation of Employee/Self will result in removal of entire family from the policy.
                                </Alert>
                            </Grid>
                        }


                        {showDeleteOption && formik.values.action == 'Delete' && (

                            <Grid item xs={12}>
                                <FormControl
                                    component="fieldset"
                                // error={
                                //     formik.touched.deleteGroupingHeader &&
                                //     Boolean(formik.errors.deleteGroupingHeader)
                                // }
                                >
                                    <FormLabel sx={{
                                        // fontWeight: 'bold',
                                        color: '#716b6b',
                                        '&.Mui-focused': {
                                            color: '#716b6b',
                                        },
                                    }}>
                                        Delete Grouping Header  <span style={{ color: 'red', fontWeight: 'normal' }}>*</span>
                                    </FormLabel>

                                    <FormGroup row>
                                        {deleteGroupingOptions.map((item) => {
                                            const value = item.value;

                                            return (
                                                <FormControlLabel
                                                    key={value}
                                                    control={
                                                        <Checkbox
                                                            checked={formik.values.deleteGroupingHeader.includes(value)}
                                                            onChange={(e) => {
                                                                const current =
                                                                    formik.values.deleteGroupingHeader || [];

                                                                if (e.target.checked) {
                                                                    formik.setFieldValue(
                                                                        "deleteGroupingHeader",
                                                                        [...current, value]
                                                                    );
                                                                } else {
                                                                    formik.setFieldValue(
                                                                        "deleteGroupingHeader",
                                                                        current.filter(
                                                                            (selected) => selected !== value
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={item.label}
                                                />
                                            );
                                        })}
                                    </FormGroup>

                                    <FormHelperText>
                                        {formik.touched.deleteGroupingHeader &&
                                            formik.errors.deleteGroupingHeader}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                        )}

                      
                        {/* File Upload */}
                        <Grid item xs={12}>
                            <FormControl size="small" fullWidth style={{ marginTop: '10px' }}>
                                <InputLabel style={{ marginTop: '-30px', marginLeft: '-15px' }}>Upload records (Excel,CSV)  <span style={{ color: 'red' }}>*</span></InputLabel>
                                <input
                                    style={{ marginTop: '5px' }}
                                    ref={fileRef}
                                    type="file"
                                    name="attachment"
                                    accept=".xlsx, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv"
                                    onChange={(event) => {
                                        formik.setFieldValue('attachment', event.currentTarget.files[0]);
                                        setSaveExcel(false);
                                        setValidateExcel(true);
                                    }}
                                />
                                <FormHelperText error={formik.touched.attachment && Boolean(formik.errors.attachment)}>
                                    {formik.touched.attachment && formik.errors.attachment}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Errors Accordion (same as before) */}
                    <Grid item xs={12}>
                        {errorObjects?.length > 0 && (

                            <Accordion style={{ marginTop: 30 }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel1a-content"
                                    id="panel1a-header"
                                >
                                    <Typography>
                                        <Badge badgeContent={
                                            errorObjects
                                                ? errorObjects.length
                                                : 0
                                        } color="primary">
                                            <ErrorIcon color="error" />
                                        </Badge>
                                        &nbsp;&nbsp;&nbsp; The following excel are missing some mandatory fields
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Alert severity="error" icon={false}>

                                        {errorObjects?.map((data) => {
                                            return <p>Row{' '}
                                                {data['row']} :- Missing or empty mandatory fields  {(data?.missingValues?.map(item => item.header)).join(',')}</p>;
                                        })}
                                    </Alert>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {errorType?.length > 0 && (

                            <Accordion style={{ marginTop: 30 }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel1a-content"
                                    id="panel1a-header"
                                >
                                    <Typography>
                                        <Badge badgeContent={
                                            errorType
                                                ? errorType.length
                                                : 0
                                        } color="primary">
                                            <ErrorIcon color="error" />
                                        </Badge>
                                        &nbsp;&nbsp;&nbsp; The following Excel columns are type validation
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Alert severity="error" icon={false}>
                                        {errorType?.map((data) => {
                                            return <p>Row{' '}:-
                                                {data['row']}    {data?.errors?.map((item) => {


                                                    return item.dropDownFlag ? <p>Please select a valid{' '} '{item.header}' from the dropdown!</p> : <p>Field{' '} '{item.header}' should be of type '{item.dataType}'</p>
                                                    //   return <p>Field{' '} '{item.header}' should be of type '{item.dataType}'</p>
                                                })}</p>;
                                        })}
                                    </Alert>
                                </AccordionDetails>
                            </Accordion>
                        )}

                    </Grid>

                    {formik.values.action === "Delete" &&
                        validatedResponse?.summary && (
                            <Grid item xs={12} style={{ marginTop: '45px' }}>
                                <Alert
                                    severity={
                                        validatedResponse.summary.matchedCount > 0
                                            ? "warning"
                                            : "error"
                                    }
                                >
                                    <Typography variant="subtitle2">
                                        Delete Confirmation
                                    </Typography>

                                    {validatedResponse.summary.matchedCount > 0 ? (
                                        <>
                                            <Typography sx={{ mt: 1 }}>
                                                <strong>
                                                    {validatedResponse.summary.matchedCount}
                                                </strong>{" "}
                                                record(s) will be deleted based on:
                                            </Typography>

                                            <ul style={{ marginTop: 8 }}>
                                                {formik.values.deleteGroupingHeader?.includes(
                                                    "employeeNo"
                                                ) && <li>Employee Number</li>}

                                                {formik.values.deleteGroupingHeader?.includes(
                                                    "tpaId"
                                                ) && <li>TPA ID</li>}
                                            </ul>

                                            {validatedResponse.summary.unmatchedCount > 0 && (
                                                <Typography>
                                                    <strong>
                                                        {
                                                            validatedResponse.summary
                                                                .unmatchedCount
                                                        }
                                                    </strong>{" "}
                                                    record(s) could not be matched and
                                                    will not be deleted.
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography>
                                            No matching records found for deletion.
                                        </Typography>
                                    )}
                                </Alert>
                            </Grid>
                        )}




                    < Grid item xs={12} >
                        <Stack direction="row" justifyContent="flex-end" mt={3} spacing={2}>
                            {validateExcel ? (
                                <LoadingButton loading={loader} variant="contained" type="submit">Validate Excel</LoadingButton>
                            ) : saveExcel ? (
                                <LoadingButton loading={loader} variant="contained" type="button" onClick={saveUploadedFile}
                                    disabled={
                                        formik.values.action === "Delete" &&
                                        validatedResponse?.summary?.matchedCount === 0
                                    }
                                >Save</LoadingButton>
                            ) : null}
                            <LoadingButton type="button" variant="contained" color="error" onClick={closeWindow}>Cancel</LoadingButton>
                        </Stack>
                    </Grid>
                </DialogContent>
            </form>
        </Dialog >
    );
};

export default UploadSumInsuredGroupWithAction;
